import { useCallback, useMemo } from 'react';
import { useOidc, useOidcAccessToken, useOidcIdToken } from '@axa-fr/react-oidc';
import { config } from '../utils/config';

/**
 * Compatibility adapter that preserves the @axa-fr/react-oidc-context v3 `useReactOidc()`
 * shape - `{ oidcUser: { access_token, profile }, logout }` - on top of the maintained
 * @axa-fr/react-oidc v7 hooks. This is the single place the v3-to-v7 mapping lives, so the
 * app's OIDC consumers only had to switch their import, not rewrite each token/claim read.
 *
 * `profile` maps to the decoded id_token payload (where the OIDC claims live: sub, email,
 * selectedcustomerid, legacy_id, etc.). `events` is null - multi-tab sign-out is handled by
 * App.js's OidcProvider onLogoutFromAnotherTab, so the old events subscription is a no-op.
 */
export function useReactOidc() {
  const { accessToken } = useOidcAccessToken();
  const { idTokenPayload } = useOidcIdToken();
  const { logout: oidcLogout } = useOidc();

  const oidcUser = useMemo(
    () =>
      accessToken
        ? { access_token: accessToken, profile: idTokenPayload || {} }
        : undefined,
    [accessToken, idTokenPayload]
  );

  // v7's logout() defaults an omitted callback to the CURRENT page, not the app's configured
  // post-logout URL like v3 did - preserve that default only when the caller passes nothing.
  // No caller left in the app: signing out belongs to CenterPoint (PAM-180).
  const logout = useCallback(
    (callbackUrl, extras) =>
      oidcLogout(callbackUrl !== undefined ? callbackUrl : config.postLogoutRedirectUri, extras),
    [oidcLogout]
  );

  return { oidcUser, logout, events: null };
}
